import { Box, Button, Flex, Grid, Image, Spinner, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import React from 'react'
import { ACAO, APAGAR, PropsLayoutTotem, TECLAS } from './tipos'

const MotionFlex = motion(Flex)

/**
 * Largura do teclado e dos blocos alinhados a ele.
 *
 * Em pé (totem, 1080x1920) o 94vw manda: o teclado ocupa quase toda a largura, que é o que
 * o dedo precisa a um braço de distância. Deitado (monitor de mesa) o 62vh assume e impede
 * que vire uma faixa larga e baixa, sem teto fixo em pixel que volte a limitar telas grandes.
 */
const LARGURA = 'min(94vw, 62vh)'

/**
 * Teclado ocupando quase toda a largura, sem texto de instrução.
 *
 * Feito para o totem em pé, tocado de longe: prioriza área de toque sobre explicação. É a
 * alternativa ao arranjo clássico — fica disponível pela configuração do evento, não some.
 */
const LayoutAmplo: React.FC<PropsLayoutTotem> = ({
  logo,
  nomeEvento,
  rotulo,
  documento,
  erro,
  buscando,
  onTecla,
  cores,
}) => (
  <MotionFlex
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    h="100dvh"
    overflow="hidden"
    bg={cores.primaria}
    color="white"
    direction="column"
    align="center"
    justify="flex-start"
    px="2vw"
    pt="0.75vh"
    pb="1.5vh"
    gap="1.5vh"
  >
    {/*
      Tudo deriva do viewport, não de breakpoint do Chakra: numa tela de 1080 de largura um
      limite fixo em 620px deixaria metade vazia e botões pequenos demais para tocar em pé.
      As fontes usam vmin — a menor dimensão — e por isso crescem com a tela sem estourar
      quando ela é estreita.

      A logo tem altura própria e o conteúdo empilha a partir do topo: centralizar devolveria
      para cima o espaço que o teclado deve ocupar.
    */}
    {logo && (
      <Box
        flex="0 1 auto"
        minH={0}
        maxH="clamp(80px, 17vh, 340px)"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Image src={logo} alt={nomeEvento} maxH="100%" maxW="min(88vw, 100%)" objectFit="contain" />
      </Box>
    )}

    <Box textAlign="center" flexShrink={0}>
      <Text fontSize="clamp(1rem, 3.4vmin, 2.5rem)" opacity={0.9}>
        Digite seu <strong>{rotulo}</strong>
      </Text>
    </Box>

    <Box
      w={LARGURA}
      flexShrink={0}
      bg="whiteAlpha.200"
      borderRadius="xl"
      px="3vmin"
      textAlign="center"
      minH="clamp(60px, 8.5vh, 160px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text fontSize="clamp(2rem, 8vmin, 6rem)" fontWeight="bold" letterSpacing="wider" lineHeight="1">
        {documento || '—'}
      </Text>
    </Box>

    {erro && (
      <Box bg="red.600" px="3vmin" py="1.5vh" borderRadius="md" w={LARGURA} flexShrink={0}>
        <Text fontSize="clamp(1rem, 3.2vmin, 2.25rem)" textAlign="center" fontWeight="600">
          {erro}
        </Text>
      </Box>
    )}

    <Grid templateColumns="repeat(3, 1fr)" gap="1.6vmin" w={LARGURA} flexShrink={0}>
      {TECLAS.map(t => {
        const acao = t === ACAO
        return (
          <Button
            key={t}
            onClick={() => onTecla(t)}
            isDisabled={buscando}
            h="clamp(60px, 14vh, 300px)"
            fontSize={acao ? 'clamp(1.1rem, 4vmin, 3rem)' : 'clamp(1.6rem, 6.5vmin, 4.5rem)'}
            fontWeight="bold"
            borderRadius="lg"
            bg={acao ? cores.secundaria : 'whiteAlpha.300'}
            color="white"
            _hover={{ bg: acao ? cores.secundaria : 'whiteAlpha.400' }}
            _active={{ bg: cores.destaque, transform: 'scale(0.97)' }}
          >
            {buscando && acao ? <Spinner size="lg" /> : acao ? 'Buscar' : t === APAGAR ? APAGAR : t}
          </Button>
        )
      })}
    </Grid>

    <Text fontSize="clamp(0.8rem, 2.2vmin, 1.5rem)" opacity={0.75} textAlign="center" flexShrink={0}>
      Não tem CPF ou não encontrou seu cadastro? Procure a secretaria.
    </Text>
  </MotionFlex>
)

export default LayoutAmplo
